<template>
  <div style="height: 100%;">
    <div style="display: flex; flex-direction: column; height: 100%;">
      <div style="flex: 1 1;">
        <v-chart
          ref="chart" 
          :option="mergedOptionsPlusLegendColors"
          :update-options="chartUpdateOptions"
          @finished="rendered = true"
        />
      </div>
      <div style="overflow: hidden;">
        <visualization-legend
          :style="'flex: 0 0;' + (visualizationOptions.display.showLegend !== true ? ' display: none;' : '')"
          v-model="colors"
          chart-ref-name="chart"
          :legend-options="chartOptionsLegend"
          :series="chartSeries"
          :rendered="rendered"
        >
        </visualization-legend>
      </div>
    </div>
  </div>
</template>

<style>
.echarts {
  width: 100%;
  height: 100%;
}
</style>

<script>
import moment from 'moment';
import { round } from 'lodash-es';
import VisualizationLegend from '@/components/VisualizationLegend';

const AUTO_MIN = (value) => Math.floor(value.min - ((value.max - value.min) * 0.1));
const AUTO_MAX = (value) => Math.ceil(value.max + ((value.max - value.min) * 0.1));

const DEFAULT_MEASUREMENT_DEFINITION = (name) => {
  return {
    alias: name,
    precision: 1,
  };
};

export default {
  name: 'visualization-e-chart',

  components: {
    VisualizationLegend,
  },

  props: {
    data: {
      type: Array,
    },
    customOptions: {
      type: Object,
    },
    seriesOptions: {
      type: Object,
    },
    visualizationOptions: {
      type: Object,
    },
    stations: {
      type: Array,
    },
    resizeTrigger: {
      type: Boolean,
    },
    showLoading: {
      type: Boolean,
    },
    gridMaxCols: {
      type: Number,
    },
    gridWidth: {
      type: Number,
    },
    gridX: {
      type: Number,
    },
    uomUnits: {
      type: Array,
      default: [],
    },
  },

  data() {
    return {
      rendered : false,
      formattedData: [],
      colors: [],
      chartUpdateOptions : {
        notMerge: true
      },
      resizeIntervalHandle: null,
    };
  },

  computed: {
    mergedOptions() {
      const topPaddingForTitles = (this.visualizationOptions.display.title ? 20 : 0) +
        (this.visualizationOptions.display.subtitle ? 20 : 0);

      const topPaddingForAxisLabel = (this.visualizationOptions.rightAxis &&
        this.visualizationOptions.rightAxis.show &&
        this.visualizationOptions.rightAxis.label &&
        this.visualizationOptions.rightAxis.alignLabel === 'end') ? 20 :
        ((this.visualizationOptions.leftAxis &&
        this.visualizationOptions.leftAxis.show &&
        this.visualizationOptions.leftAxis.label &&
        this.visualizationOptions.leftAxis.alignLabel === 'end') ? 20 : 0);

      const additionalTopPadding = Math.max(topPaddingForTitles, topPaddingForAxisLabel);

      const defaultOptions = {
        title: {
          text: this.visualizationOptions.display.title || undefined,
          subtext: this.visualizationOptions.display.subtitle || undefined,
          left: 'center',
        },
        legend: this.chartOptionsLegend,
        xAxis: {
          type: 'time',
          show: this.visualizationOptions.xAxis ? this.visualizationOptions.xAxis.show : false,
          splitNumber: this.gridWidth < 6 ? 1 : (this.gridWidth < 12 ? 3 : 5), // TODO: RE-EVALUATE
        },
        yAxis: [
          {
            type: 'value',
            position: 'left',
            show: this.visualizationOptions.leftAxis ? this.visualizationOptions.leftAxis.show : true,
            min: (this.visualizationOptions.leftAxis && this.visualizationOptions.leftAxis.min) || AUTO_MIN,
            max: (this.visualizationOptions.leftAxis && this.visualizationOptions.leftAxis.max) || AUTO_MAX,
            name: (this.visualizationOptions.leftAxis && this.visualizationOptions.leftAxis.label) || '',
            nameLocation: this.visualizationOptions.leftAxis ? this.visualizationOptions.leftAxis.alignLabel : undefined,
            nameGap: this.bestGuessNameGap(this.visualizationOptions.leftAxis, this.chartSeries),
          },
          {
            type: 'value',
            position: 'right',
            show: this.visualizationOptions.rightAxis ? this.visualizationOptions.rightAxis.show : true,
            min: (this.visualizationOptions.rightAxis && this.visualizationOptions.rightAxis.min) || AUTO_MIN,
            max: (this.visualizationOptions.rightAxis && this.visualizationOptions.rightAxis.max) || AUTO_MAX,
            name: (this.visualizationOptions.rightAxis && this.visualizationOptions.rightAxis.label) || '',
            nameLocation: this.visualizationOptions.rightAxis ? this.visualizationOptions.rightAxis.alignLabel : undefined,
            nameGap: this.visualizationOptions.rightAxis ? (this.visualizationOptions.rightAxis.alignLabel === 'center' ? 30 : 15) : undefined,
          },
        ],
        grid: {
          left: 20 + (this.visualizationOptions.leftAxis && this.visualizationOptions.leftAxis.alignLabel === 'center' ? 20 : 0) + 'px',
          right: '20px',
          bottom: '20px',
          top: 20 + additionalTopPadding + 'px',
          containLabel: true,
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'cross'
          },
          appendToBody: true,
          position: (point, params, dom, rect, size) => {
            const chartPercentX = point[0] / size.viewSize[0];
            const gridPercentX = (chartPercentX * this.gridWidth + this.gridX) / this.gridMaxCols;

            if (gridPercentX > 0.5) {
              return [ point[0] - 20 - dom.clientWidth, point[1] + 10 ];
            } else {
              return [ point[0] + 20, point[1] + 10 ];
            }
          },
          formatter: (params, ticket, callback) => {
            if (!params[0]) params = [params];

            let tooltip = `${moment(params[0].value[0]).format('YYYY-MM-DD HH:mm:ss')}`;

            for (const series of params) {
              const measurementDefinition = this.formattedData.find(e => e.id === series.seriesId).measurementDefinition;
              const roundedValue = round(series.value[1], measurementDefinition.precision);
              
              let unitText = '';
              if (measurementDefinition.unit_id) {
                const unit = this.uomUnits.find(e => e.unit_id === measurementDefinition.unit_id);

                if (unit) {
                  unitText = unit.symbol;
                }
              }

              tooltip += `<br><span style="display:inline-block;margin-right:5px;border-radius:10px;width:10px;height:10px;background-color:${series.color};"></span>`;
              tooltip += `${series.seriesName}: ${roundedValue} ${unitText}`;
            }

            return tooltip;
          },
        }
      };

      if (this.customOptions.echartsOptionsModifier) {
        this.customOptions.echartsOptionsModifier(defaultOptions, {
          visualizationOptions: this.visualizationOptions,
          seriesOptions: this.seriesOptions,
          formattedData: this.formattedData,
          uomUnits: this.uomUnits,
        });
      }

      defaultOptions.series = this.chartSeries;

      return defaultOptions;
    },
    mergedOptionsPlusLegendColors() {
      return {
        ...this.mergedOptions,
        color: this.colors,
      };
    },
    chartSeries() {
      if (this.visualizationOptions.thresholdLines) {
        const markLines = {
          id: 'markLines',
          data: [],
          type: 'line',
          markLine: {
            silent: true,
            symbol: 'none',
            data: this.visualizationOptions.thresholdLines.map(e => { return {
              yAxis: e.value,
              lineStyle: {
                width: e.size - 0.5,
                color: e.color,
              },
              label: {
                show: true,
                position: 'insideEndBottom',
              },
            }}),
          },
        };

        return [ ...this.formattedData, markLines ];
      } else {
        return this.formattedData;
      }
    },
    chartOptionsLegend() {
      const legend = {
        data: [],
        show: false,
        bottom: 0,
        showCustom: true,
        orient: 'horizontal',
      };

      for (const meas of this.data) {
        // if (this.seriesOptions[meas.key] && this.seriesOptions[meas.key].showInLegend) {
          const station = this.stations.find(e => e.id === meas.station);
          const measurementDefinition = station.sensor_definitions
            .find(e => e.name === meas.measurement) || DEFAULT_MEASUREMENT_DEFINITION(meas.measurement);
          legend.data.push({
            name: `${station.name} : ${measurementDefinition.alias}`,
          });
        // }
      }

      return legend;
    },
  },

  methods: {
    bestGuessNameGap(axisOptions, series) {
      if (!axisOptions) return undefined;

      if (axisOptions.alignLabel === 'center') {
        let digits = 2;

        if (axisOptions.min) {
          digits = Math.max(digits, Math.round(Number(axisOptions.min)).toFixed(0).length);
        }
        if (axisOptions.max) {
          digits = Math.max(digits, Math.round(Number(axisOptions.max)).toFixed(0).length);
        }

        if (series) {
          for (const singleSeries of series) {
            // guess using the first data point for efficiency's sake
            // NOTE: might need to change to cycle through all points (way less efficient)
            if (singleSeries.data[0] && !isNaN(singleSeries.data[0][1])) {
              digits = Math.max(digits, Math.round(Number(singleSeries.data[0][1])).toFixed(0).length);
            }
          }
        }
        
        return digits * 8 + 15;
      } else {
        return 15;
      }
    },
    refreshMeasurementData() {
      const newFormattedData = [];

      for (const dataSet of this.data) {
        const station = this.stations.find(e => e.id === dataSet.station);
        const measurementDefinition = station.sensor_definitions
          .find(e => e.name === dataSet.measurement) || DEFAULT_MEASUREMENT_DEFINITION(dataSet.measurement);

        const mData = {
          itemStyle: {
            color: this.seriesOptions[dataSet.key] ? this.seriesOptions[dataSet.key].color : undefined,
          },
          lineStyle: {
            width: this.seriesOptions[dataSet.key] ? this.seriesOptions[dataSet.key].size : 2,
            type: this.seriesOptions[dataSet.key] ? this.seriesOptions[dataSet.key].lineStyle : undefined,
          },
          symbolSize: this.seriesOptions[dataSet.key] ? this.seriesOptions[dataSet.key].size + 2 : 6,
          id: dataSet.key,
          name: (this.seriesOptions[dataSet.key] && this.seriesOptions[dataSet.key].label) ? this.seriesOptions[dataSet.key].label :
            `${station.name} : ${measurementDefinition.alias}`,
          type: this.customOptions.seriesType,
          showSymbol: false,
          data: dataSet.data,
          yAxisIndex: 0,//this.seriesOptions[dataSet.key] ? (this.seriesOptions[dataSet.key].yAxis === 'left' ? 0 : 1) : 0,
          measurementDefinition,
        };

        if (this.customOptions.echartsSeriesModifier) {
          this.customOptions.echartsSeriesModifier(mData, {
            station,
            seriesOptions: this.seriesOptions[dataSet.key],
            visualizationOptions: this.visualizationOptions,
            dataSet,
            seriesCount: this.data.length,
            uomUnits: this.uomUnits,
          });
        }

        newFormattedData.push(mData);
      }

      this.formattedData = newFormattedData;
    },
    exportBase64() {
      return this.$refs.chart.getDataURL({
        pixelRatio: 2,
        backgroundColor: '#fff',
      });
    },
  },

  watch: {
    data(newVal, oldVal) {
      this.refreshMeasurementData();
    },
    customOptions(newVal, oldVal) {
      this.refreshMeasurementData();
    },
    seriesOptions(newVal, oldVal) {
      this.refreshMeasurementData();
    },
    visualizationOptions(newVal, oldVal) {
      this.refreshMeasurementData();
    },
  },
  
  created() {
    // resize every 500ms when applicable. Tried doing this with
    // event handlers on resize, container-resize, etc.. but it was
    // CPU intensive and locked up the UI
    this.resizeIntervalHandle = setInterval(() => {
      if (this.$refs.chart && this.resizeTrigger) {
        this.$refs.chart.resize();
      }
    }, 500);
  },

  destroyed() {
    clearInterval(this.resizeIntervalHandle);
  },

  mounted() {
    this.refreshMeasurementData();
  },
};
</script>